import React from 'react';
import { useAuth } from '../../contexts/AuthContext';

/**
 * PermissionGuard - Controls access to UI components based on user permissions
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Components to render if access is granted
 * @param {string} [props.role] - Required role ('administrator', 'franchisee', 'operator')
 * @param {string} [props.permission] - Required permission key
 * @param {string} [props.franchiseId] - Franchise ID for franchise-specific permissions
 * @param {React.ReactNode} [props.fallback] - Component to render if access is denied
 * @param {boolean} [props.requireActive=true] - Whether to require active user status
 * @param {function} [props.customCheck] - Custom permission check function
 */
export const PermissionGuard = ({ 
  children, 
  role, 
  permission, 
  franchiseId,
  fallback = null,
  requireActive = true,
  customCheck
}) => {
  const { userProfile, hasPermission, hasAccessToFranchise, loading } = useAuth();

  // Show loading state
  if (loading) {
    return fallback;
  }

  // No user profile
  if (!userProfile) {
    return fallback;
  }

  // Check if user is active (if required)
  if (requireActive && !userProfile.isActive) {
    return fallback;
  }

  // Admin override (admins can access everything unless explicitly restricted)
  const isAdmin = userProfile.globalRole === 'administrator';
  if (isAdmin && !customCheck) {
    return children;
  }

  // Role-based check
  if (role && userProfile.globalRole !== role && !isAdmin) {
    return fallback;
  }

  // Permission-based check
  if (permission && !hasPermission(permission, franchiseId) && !isAdmin) {
    return fallback;
  }

  // Franchise access check
  if (franchiseId && !hasAccessToFranchise(franchiseId) && !isAdmin) {
    return fallback;
  }

  // Custom check function
  if (customCheck && !customCheck(userProfile)) {
    return fallback;
  }

  return children;
};

/**
 * RoleGuard - Simplified guard for role-based access
 */
export const RoleGuard = ({ children, roles, fallback = null }) => {
  const { userProfile, loading } = useAuth();

  if (loading || !userProfile) {
    return fallback;
  }

  const allowedRoles = Array.isArray(roles) ? roles : [roles];
  const hasAccess = allowedRoles.includes(userProfile.globalRole) || 
                   userProfile.globalRole === 'administrator';

  return hasAccess ? children : fallback;
};

/**
 * AdminGuard - Quick guard for admin-only content
 */
export const AdminGuard = ({ children, fallback = null }) => {
  return (
    <RoleGuard roles="administrator" fallback={fallback}>
      {children}
    </RoleGuard>
  );
};

/**
 * FranchiseeGuard - Guard for franchisee and admin access
 */
export const FranchiseeGuard = ({ children, fallback = null }) => {
  return (
    <RoleGuard roles={['administrator', 'franchisee']} fallback={fallback}>
      {children}
    </RoleGuard>
  );
};

/**
 * OperatorGuard - Guard for operator access
 */
export const OperatorGuard = ({ children, fallback = null }) => {
  return (
    <RoleGuard roles="operator" fallback={fallback}>
      {children}
    </RoleGuard>
  );
};

/**
 * AuthenticatedGuard - Guard for any authenticated user
 */
export const AuthenticatedGuard = ({ children, fallback = null }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return fallback;
  }

  return user ? children : fallback;
};

/**
 * DeviceToolGuard - Guard for device tool permissions
 */
export const DeviceToolGuard = ({ toolType, children, fallback = null }) => {
  const { userProfile } = useAuth();
  
  if (!userProfile) {
    return fallback || <div>Loading...</div>;
  }

  // Admin has access to all tools
  if (userProfile.globalRole === 'administrator') {
    return children;
  }

  // Check permissions for device tools
  const deviceToolPermissions = userProfile.permissions?.deviceTools || [];
  
  // Map tool types to permission requirements (support both old and new names)
  const toolPermissions = {
    'general': ['favorites', 'general'],    // support both old and new
    'favorites': ['favorites', 'general'],   // support both old and new
    'sensor': ['hardware', 'sensor'],       // support both old and new
    'hardware': ['hardware', 'sensor'],     // support both old and new
    'advanced': ['advanced'],
    'diagnostics': ['advanced'],
    'maintenance': ['advanced']
  };

  const requiredPermissions = toolPermissions[toolType] || [];
  const hasPermission = requiredPermissions.some(permission => 
    deviceToolPermissions.includes(permission)
  );

  return hasPermission ? children : (fallback || <div>Access denied - insufficient device tool permissions</div>);
};

export default PermissionGuard; 